/**
 * Discordance Dialog — view and update discordance state for a study.
 *
 * Reads `study.discordance.nextState` to determine available actions,
 * then calls `updateDiscordance` with appropriate params.
 *
 * State machine:
 *   ERCALL   → Coordinator/Physician can make ER diagnosis call
 *   RADCALL  → Radiologist can agree/disagree with ER finding
 *   ACK      → Coordinator acknowledges rad's response
 *   VIEW     → View-only (already resolved)
 *   NA       → Not applicable / not enabled
 */
import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Typography, Chip,
  FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox,
  Divider, List, ListItem, ListItemText,
} from '@mui/material';
import { api } from '../../api/rpc';
import type { Study } from '../../api/types';
import { gwtStyles } from '../../config/theme';
import { pushError, pushSuccess } from '../../stores/notificationStore';

interface DiscordanceDialogProps {
  open: boolean;
  study: Study | null;
  onClose: () => void;
  onComplete: () => void;
}

const URGENCY_OPTIONS = [
  { value: 'NORMAL', label: 'Normal' },
  { value: 'ABNORMALCRIT', label: 'Abnormal – Critical' },
  { value: 'ABNORMALNONCRIT', label: 'Abnormal – Non-Critical' },
];

const RAD_CALL_OPTIONS = [
  { value: 'AGREE', label: 'Agree' },
  { value: 'DISAGREE', label: 'Disagree' },
  { value: 'STRONGLYDISAGREE', label: 'Strongly Disagree' },
];

export const DiscordanceDialog = ({ open, study, onClose, onComplete }: DiscordanceDialogProps) => {
  const [saving, setSaving] = useState(false);
  // ER call fields
  const [callDiagnosis, setCallDiagnosis] = useState('');
  const [callUrgency, setCallUrgency] = useState('');
  const [callMade, setCallMade] = useState(false);
  // Rad call fields
  const [radCall, setRadCall] = useState('');
  const [note, setNote] = useState('');
  // Action needed
  const [actionNeeded, setActionNeeded] = useState(false);

  const disc = study?.discordance;
  const nextState = disc?.nextState ?? 'NA';

  useEffect(() => {
    if (open) {
      setCallDiagnosis(disc?.callDiagnosis ?? '');
      setCallUrgency(disc?.callUrgency ?? '');
      setCallMade(disc?.callMade ?? false);
      setRadCall(disc?.radCall ?? '');
      setNote('');
      setActionNeeded(disc?.actionNeeded ?? false);
    }
  }, [open, disc]);

  const handleSubmit = async () => {
    if (!study) return;
    setSaving(true);
    try {
      if (nextState === 'ERCALL') {
        await api.updateDiscordance({
          objKey: study.objKey,
          callDiagnosis,
          callUrgency: callUrgency || undefined,
          callMade,
          actionNeeded,
          note: note || undefined,
        });
      } else if (nextState === 'RADCALL') {
        if (!radCall) {
          pushError('Please select Agree, Disagree, or Strongly Disagree');
          setSaving(false);
          return;
        }
        if ((radCall === 'DISAGREE' || radCall === 'STRONGLYDISAGREE') && !note.trim()) {
          pushError('A note is required when disagreeing');
          setSaving(false);
          return;
        }
        await api.updateDiscordance({
          objKey: study.objKey,
          radCall,
          note: note || undefined,
        });
      } else if (nextState === 'ACK') {
        await api.discordanceViewed(study.objKey);
      } else if (nextState === 'VIEW') {
        await api.discordanceViewed(study.objKey);
      }
      pushSuccess('Discordance updated');
      onComplete();
      onClose();
    } catch (e) {
      pushError('Failed to update discordance: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSaving(false);
    }
  };

  const stateLabel: Record<string, string> = {
    ERCALL: 'ER Diagnosis Call',
    RADCALL: 'Radiologist Response',
    ACK: 'Acknowledge',
    VIEW: 'View Only',
    NA: 'Not Applicable',
  };

  const stateColor: Record<string, 'error' | 'warning' | 'info' | 'success' | 'default'> = {
    ERCALL: 'error',
    RADCALL: 'warning',
    ACK: 'info',
    VIEW: 'success',
    NA: 'default',
  };

  const formatDate = (ts: number) => {
    if (!ts) return '';
    return new Date(ts).toLocaleString();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '6px',
          border: `1px solid ${gwtStyles.borderSubtle}`,
          backgroundColor: gwtStyles.popupBg,
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle sx={{ fontSize: 14, fontWeight: 600, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <span>Discordance{study ? ` — ${study.name}` : ''}</span>
        <Chip
          label={stateLabel[nextState] ?? nextState}
          size="small"
          color={stateColor[nextState] ?? 'default'}
          sx={{ fontSize: 11 }}
        />
      </DialogTitle>
      <DialogContent>
        {/* Current state summary */}
        {disc?.callDiagnosis && (
          <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'action.hover', borderRadius: '4px' }}>
            <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
              Current ER Call
            </Typography>
            <Typography sx={{ fontSize: 12 }}>
              Diagnosis: {disc.callDiagnosis}
              {disc.callUrgency && ` (${URGENCY_OPTIONS.find((o) => o.value === disc.callUrgency)?.label ?? disc.callUrgency})`}
            </Typography>
            {disc.radCall && (
              <Typography sx={{ fontSize: 12, mt: 0.5 }}>
                Radiologist: {RAD_CALL_OPTIONS.find((o) => o.value === disc.radCall)?.label ?? disc.radCall}
              </Typography>
            )}
            {disc.note && (
              <Typography sx={{ fontSize: 12, mt: 0.5, fontStyle: 'italic' }}>{disc.note}</Typography>
            )}
          </Box>
        )}

        {/* ER Call form */}
        {nextState === 'ERCALL' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TextField
              label="Diagnosis"
              size="small"
              fullWidth
              value={callDiagnosis}
              onChange={(e) => setCallDiagnosis(e.target.value)}
              sx={{ '& .MuiInputBase-root': { fontSize: 13 } }}
            />
            <FormControl size="small" fullWidth>
              <InputLabel sx={{ fontSize: 13 }}>Urgency</InputLabel>
              <Select
                value={callUrgency}
                label="Urgency"
                onChange={(e) => setCallUrgency(e.target.value)}
                sx={{ fontSize: 13 }}
              >
                {URGENCY_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13 }}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Checkbox size="small" checked={callMade} onChange={(e) => setCallMade(e.target.checked)} />}
              label="Call made"
              slotProps={{ typography: { sx: { fontSize: 13 } } }}
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={actionNeeded} onChange={(e) => setActionNeeded(e.target.checked)} />}
              label="Action needed"
              slotProps={{ typography: { sx: { fontSize: 13 } } }}
            />
            <TextField
              label="Note (optional)"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              sx={{ '& .MuiInputBase-root': { fontSize: 13 } }}
            />
          </Box>
        )}

        {/* Radiologist response form */}
        {nextState === 'RADCALL' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <FormControl size="small" fullWidth>
              <InputLabel sx={{ fontSize: 13 }}>Your Response</InputLabel>
              <Select
                value={radCall}
                label="Your Response"
                onChange={(e) => setRadCall(e.target.value)}
                sx={{ fontSize: 13 }}
              >
                {RAD_CALL_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13 }}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={radCall === 'DISAGREE' || radCall === 'STRONGLYDISAGREE' ? 'Note (required)' : 'Note (optional)'}
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              sx={{ '& .MuiInputBase-root': { fontSize: 13 } }}
            />
          </Box>
        )}

        {/* Acknowledge button */}
        {nextState === 'ACK' && (
          <Typography variant="body2" sx={{ fontSize: 13, py: 1 }}>
            Click Submit to acknowledge the radiologist's response.
          </Typography>
        )}

        {/* View-only */}
        {nextState === 'VIEW' && (
          <Typography variant="body2" sx={{ fontSize: 13, py: 1 }}>
            This discordance has been resolved. Click Submit to mark as viewed.
          </Typography>
        )}

        {/* NA */}
        {nextState === 'NA' && (
          <Typography variant="body2" sx={{ fontSize: 13, py: 1, color: 'text.secondary' }}>
            Discordance is not applicable for this study.
          </Typography>
        )}

        {/* Audit trail */}
        {disc?.actions && disc.actions.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
              History
            </Typography>
            <List dense sx={{ border: `1px solid ${gwtStyles.borderSubtle}`, borderRadius: '4px', maxHeight: 200, overflow: 'auto' }}>
              {disc.actions.map((a, i) => (
                <ListItem key={i}>
                  <ListItemText
                    primary={`${a.action ?? 'Unknown'}${a.callDiagnosis ? ` — ${a.callDiagnosis}` : ''}${a.radCall ? ` — ${RAD_CALL_OPTIONS.find((o) => o.value === a.radCall)?.label ?? a.radCall}` : ''}`}
                    secondary={`${a.user ?? ''} — ${a.date ? formatDate(a.date) : ''}${a.note ? ` — ${a.note}` : ''}`}
                    primaryTypographyProps={{ fontSize: 12 }}
                    secondaryTypographyProps={{ fontSize: 11 }}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        <Button size="small" onClick={onClose} sx={{ textTransform: 'none', fontSize: 12 }}>
          Cancel
        </Button>
        {nextState !== 'NA' && (
          <Button
            size="small"
            variant="contained"
            disabled={saving}
            onClick={handleSubmit}
            sx={{ textTransform: 'none', fontSize: 12 }}
          >
            {saving ? 'Saving…' : 'Submit'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
